import {Injectable} from '@angular/core';
import {AngularFirestore} from "@angular/fire/compat/firestore";
import {NotificationType} from "../../enums/notification-type.enum";
import {BaseUserService} from "../base-user/base-user.service";
import {ActorService} from "../../../actor/services/actor/actor.service";
import {ActorModel} from "../../../actor/models/actor.model";
import {FirestoreService} from "../firestore/firestore.service";
import {AngularFireAuth} from "@angular/fire/compat/auth";
import {FirestoreCollectionEnum} from "../../enums/firestore-collection.enum";
import {lastValueFrom} from "rxjs";
import {FirestoreWhereClauseModel} from "../../models/firestore-where-clause.model";
import dayjs from "dayjs";
import {NotificationDoc} from "../../interfaces/notification-doc.interface";
import {NotificationDocWithId} from "../../interfaces/notification-doc-with-id.interface";
import {NotificationEntityName} from "../../enums/notification-entity-name.enum";

export type DataNotification = {
  type: NotificationType,
  entityId?: number,
  entityName?: NotificationEntityName;
  entityData?: any,
}

export type MessageNotification = {
  title: string,
  link: string,
  queryParams?: string,
  message?: string,
  recipientUserUID: string,
}

@Injectable({
  providedIn: 'root'
})
export class FirebaseNotificationService extends FirestoreService {

  constructor(
    protected readonly afs: AngularFirestore,
    protected readonly angularFireAuth: AngularFireAuth,
    private readonly actorService: ActorService,
    protected readonly baseUserService: BaseUserService
  ) {
    super(afs, angularFireAuth, baseUserService);
  }

  async sendFirebaseNotification(dataNotification: DataNotification, messageNotification: MessageNotification): Promise<any> {
    const docContent: NotificationDoc = await this.buildFirebaseNotification(dataNotification, messageNotification);
    console.log('Sending notification to firebase...', docContent);
    return await this.getCollectionRef(FirestoreCollectionEnum.notifications).add(docContent);
  }

  private async buildFirebaseNotification(dataNotification: DataNotification, messageNotification: MessageNotification) {
    const loggedUser = await this.baseUserService.loggedUser();
    const loggedActor: ActorModel = await this.actorService.loggedActor();

    const docContent: NotificationDoc = {
      type: dataNotification.type,
      entityId: dataNotification.entityId,
      entityName: dataNotification.entityName,
      entityData: {...dataNotification.entityData},

      title: messageNotification.title,
      link: messageNotification.link,
      recipientUserUID: messageNotification.recipientUserUID,
      message: messageNotification.message,
      queryParams: messageNotification.queryParams,

      createdAt: new Date(),
      createdBy: loggedUser?.id,
      createdByUsername: loggedUser?.username,
      createdByUID: loggedUser?.chatUID,
      createdByActor: loggedActor?.id,
      createdByActorDenomination: loggedActor?.denomination,
      isRead: false
    }

    return docContent;
  }

  async getNotifications(loggedUserUID: string, whereClauses: FirestoreWhereClauseModel[] = [], startAfterId?: string, limit?: number): Promise<NotificationDocWithId[]> {
    whereClauses = [
      ...this.getDefaultWhereClauses(loggedUserUID),
      ...whereClauses
    ];

    const cursor = await this.getNotificationDocumentSnapshotById(startAfterId);
    const notifications$ = this.afs.collection<NotificationDoc>(
      FirestoreCollectionEnum.notifications,
      (ref) => this.queryFirebase(ref, limit, cursor, whereClauses)
    ).get();
    const querySnapshot = await lastValueFrom(notifications$);

    return querySnapshot.docs.map(d => ({id: d.id, ...d.data()}));
  }

  async updateNotification(id: string, data: Partial<NotificationDoc>) {
    await this.afs.doc<NotificationDoc>(FirestoreCollectionEnum.notifications + '/' + id).update(data);
  }

  private async getNotificationDocumentSnapshotById(id: string) {
    if (!id) {
      return null;
    }

    const doc$ = this.afs.collection<NotificationDoc>(FirestoreCollectionEnum.notifications).doc(id).get();
    return await lastValueFrom(doc$);
  }

  async getUnreadNotificationsStateChanges$(loggedUserUID: string) {
    const whereClauses = this.getDefaultWhereClauses(loggedUserUID);

    whereClauses.push({
      field: `isRead`,
      operator: '==',
      value: false
    })

    return this.afs.collection<NotificationDoc>(FirestoreCollectionEnum.notifications,
      (ref) => this.queryFirebase(ref, undefined, undefined, whereClauses)
    ).stateChanges(['added', 'removed']);
  }

  private getDefaultWhereClauses(loggedUserUID: string): FirestoreWhereClauseModel[] {
    const oneMonthAgoDateString = dayjs().subtract(1, 'month').format('YYYY-MM-DD');
    const oneMonthAgoDate = new Date(oneMonthAgoDateString);

    return [
      {
        field: `recipientUserUID`,
        operator: '==',
        value: loggedUserUID
      },
      {
        field: 'createdAt',
        operator: '>',
        value: oneMonthAgoDate
      }
    ];
  }
}
